import { z } from "zod/v4"
import { SelectionParticipantId, SelectionParticipantIdSchema } from "../id"
import { DocumentSnapshotLike } from "../firestore"
import { CreatedAt, UpdatedAt } from "./system"
import { UserDenormalizedWithLook } from "./user"
import { MediaImage } from "../common"

export const SelectionParticipantDocumentRequest = z.object({
  image: z.string().optional(),
  description: z.string().optional(),
  media: MediaImage.optional(),
})

export const SelectionParticipantDocumentSystem = z.object({
  user: UserDenormalizedWithLook,
  shard: z.number().min(0).max(255),
})

export const SelectionParticipantDocumentMerge = z.intersection(
  SelectionParticipantDocumentRequest,
  SelectionParticipantDocumentSystem,
)

export const SelectionParticipantDocumentMeta = z.intersection(
  SelectionParticipantDocumentMerge,
  z.object({
    displaying: z.boolean()
  })
)

export type SelectionParticipantDocumentMeta = z.infer<typeof SelectionParticipantDocumentMeta>

export const SelectionParticipantDocumentBody = z.intersection(
  SelectionParticipantDocumentMeta,
  z.object({
    created_at: CreatedAt,
    updated_at: UpdatedAt,
  })
)

export type SelectionParticipantDocumentBody = z.infer<typeof SelectionParticipantDocumentBody>

export const SelectionParticipantDocument = z.intersection(
  SelectionParticipantDocumentBody,
  z.object({
    id: SelectionParticipantIdSchema,
  })
)

export type SelectionParticipantDocument = z.infer<typeof SelectionParticipantDocument>

export class SelectionParticipantDocuments {

  /** シャードの最大値（0〜255） */
  public static readonly SHARD_MAX = 256

  static toDocumentData(entity: SelectionParticipantDocument): object {
    return {
      ...entity,
      // id は document path に入るので入れない
      id: undefined,
    }
  }

  static fromDocumentSnapshot(doc: DocumentSnapshotLike | null): SelectionParticipantDocument | null {
    if (!doc) {
      return null
    }
    const data = doc.data()
    if (data === undefined) {
      return null
    }

    return {
      id: SelectionParticipantId(doc.id),
      image: data["image"],
      description: data["description"],
      media: data["media"],
      displaying: data["displaying"],
      shard: data["shard"],
      user: data["user"],
      created_at: data["created_at"],
      updated_at: data["updated_at"],
    }
  }
}

export const SelectionAppealKind = z.union([
  z.literal("camera"),
  z.literal("image"),
])

export const AddSelectionSequenceAppealRequest = z.object({
  kind: SelectionAppealKind,
})

export type AddSelectionSequenceAppealRequest = z.infer<typeof AddSelectionSequenceAppealRequest>

export const UpdateSelectionParticipantDisplayingRequest = z.object({
  displaying: z.boolean()
})

export type UpdateSelectionParticipantDisplayingRequest = z.infer<typeof UpdateSelectionParticipantDisplayingRequest>

export const SelectionParticipantNominateStatus = z.union([
  z.literal("previous"),
  z.literal("current"),
])

export type SelectionParticipantNominateStatus = z.infer<typeof SelectionParticipantNominateStatus>

export const SelectionParticipantNominateSelected = z.union([
  z.literal("never"),
  z.literal("current"),
  z.literal("previous"),
])

export type SelectionParticipantNominateSelected = z.infer<typeof SelectionParticipantNominateSelected>

export const SelectionParticipantNominateDocumentBody = z.intersection(
  SelectionParticipantDocumentMerge,
  z.object({
    appeal: AddSelectionSequenceAppealRequest.optional(),
    nominate: SelectionParticipantNominateStatus,
    selected: SelectionParticipantNominateSelected,
    created_at: CreatedAt,
    updated_at: UpdatedAt,
  })
)

export type SelectionParticipantNominateDocumentBody = z.infer<typeof SelectionParticipantNominateDocumentBody>

export const SelectionParticipantNominateDocument = z.intersection(
  SelectionParticipantNominateDocumentBody,
  z.object({
    id: SelectionParticipantIdSchema,
  })
)

export type SelectionParticipantNominateDocument = z.infer<typeof SelectionParticipantNominateDocument>

export class SelectionParticipantNominateDocuments {

  static toDocumentData(entity: SelectionParticipantNominateDocument): object {
    return {
      ...entity,
      // id は document path に入るので入れない
      id: undefined,
    }
  }

  static fromDocumentSnapshot(doc: DocumentSnapshotLike | null): SelectionParticipantNominateDocument | null {
    if (!doc) {
      return null
    }
    const data = doc.data()
    if (data === undefined) {
      return null
    }

    return {
      id: SelectionParticipantId(doc.id),
      image: data["image"],
      description: data["description"],
      shard: data["shard"],
      appeal: data["appeal"],
      nominate: data["nominate"],
      selected: data["selected"],
      user: data["user"],
      created_at: data["created_at"],
      updated_at: data["updated_at"],
    }
  }
}

export const SelectionParticipantPseudoNominateDocumentBody = z.intersection(
  SelectionParticipantDocumentSystem,
  z.object({
    created_at: CreatedAt,
    updated_at: UpdatedAt,
  })
)

export const SelectionParticipantPseudoNominateDocument = z.intersection(
  SelectionParticipantPseudoNominateDocumentBody,
  z.object({
    id: SelectionParticipantIdSchema,
  })
)

export type SelectionParticipantPseudoNominateDocument = z.infer<typeof SelectionParticipantPseudoNominateDocument>

export class SelectionParticipantPseudoNominateDocuments {

  static toDocumentData(entity: SelectionParticipantPseudoNominateDocument): object {
    return {
      ...entity,
      // id は document path に入るので入れない
      id: undefined,
    }
  }

  static fromDocumentSnapshot(doc: DocumentSnapshotLike | null): SelectionParticipantPseudoNominateDocument | null {
    if (!doc) {
      return null
    }
    const data = doc.data()
    if (data === undefined) {
      return null
    }

    return {
      id: SelectionParticipantId(doc.id),
      shard: data["shard"],
      user: data["user"],
      created_at: data["created_at"],
      updated_at: data["updated_at"],
    }
  }
}
